<template>
  <AppLayout
    :userRole="activeUserRole"
    :image-logo="defaultImage"
    header-title="Submitted Audits"
  >
    <div
      class="px-4 w-full flex flex-row justify-center gap-x-12 bg-white text-PrimaryText"
    >
      <div class="w-full pt-6">
        <!-- Filter Section -->
        <div class="bg-white border border-gray-200 rounded-lg mb-4 p-4">
          <div class="grid grid-cols-12 gap-4 items-end">
            <!-- Date Range Group - combined for tighter spacing -->
            <div class="col-span-3 flex flex-col">
              <label class="text-gray-700 font-medium mb-2">Date Range</label>
              <div class="flex items-center gap-2">
                <div class="dx-field-value flex-1">
                  <DxDateBox
                    v-model:value="filters.fromDate"
                    picker-type="rollers"
                    class="border-b border-gray-400"
                    id="fromDateFilter"
                    :max="new Date()"
                    @value-changed="onDateFilterChange"
                    placeholder="From"
                  />
                </div>
                <span class="text-gray-500 text-sm">to</span>
                <div class="dx-field-value flex-1">
                  <DxDateBox
                    v-model:value="filters.toDate"
                    picker-type="rollers"
                    class="border-b border-gray-400"
                    id="toDateFilter"
                    :max="new Date()"
                    @value-changed="onDateFilterChange"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
            
            <!-- Premise Filter -->
            <div class="col-span-2 flex flex-col">
              <label class="text-gray-700 font-medium mb-2">Premise</label>
              <b-input
                v-model="filters.premise"
                placeholder="Filter premise..."
                icon="magnify"
                @input="debouncedFilterChange"
                class="filter-input-large"
              />
            </div>
            
            <!-- Template Filter -->
            <div class="col-span-2 flex flex-col">
              <label class="text-gray-700 font-medium mb-2">Template</label>
              <b-input
                v-model="filters.templateName"
                placeholder="Filter template..."
                icon="magnify"
                @input="debouncedFilterChange"
                class="filter-input-large"
              />
            </div>
            
            <!-- Auditor Filter -->
            <div class="col-span-2 flex flex-col">
              <label class="text-gray-700 font-medium mb-2">Auditor</label>
              <b-input
                v-model="filters.auditor"
                placeholder="Filter auditor..."
                icon="magnify"
                @input="debouncedFilterChange"
                class="filter-input-large"
              />
            </div>
            
            <!-- Clear Button -->
            <div class="col-span-3 flex justify-center">
              <b-button
                type="is-light"
                @click="clearFilters"
                icon-left="close"
                class="clear-filters-btn"
              >
                Clear Filters
              </b-button>
            </div>
          </div>
        </div>
        
        <div class="premise-list rounded-md">
          <div class="text-onyx">
            <b-table
              :key="tableKey"
              :data="audits"
              :paginated="true"
              :backend-pagination="true"
              :backend-sorting="true"
              :total="totalRows"
              :per-page="perPage"
              :pagination-simple="true"
              :pagination-rounded="true"
              aria-next-label="Next page"
              aria-previous-label="Previous page"
              aria-page-label="Page"
              aria-current-label="Current page"
              :current-page.sync="currentPage"
              :default-sort="[sortBy, sortDirection]"
              @sort="onSortChanged"
              @page-change="onPageChanged"
              @per-page-change="onPerPageChanged"
            >
              <template slot-scope="props">
                <b-table-column
                  field="submit_time"
                  label="Completed Date"
                  width="18%"
                  :sortable="true"
                  centered
                >
                  {{
                    new Intl.DateTimeFormat("en-US", {
                      timeZone: "America/Denver",
                      timeStyle: "medium",
                      dateStyle: "short",
                    }).format(new Date(props.row.submit_time))
                  }}
                </b-table-column>
                <b-table-column
                  field="premises.name"
                  label="Premise"
                  width="18%"
                  :sortable="false"
                >
                  {{ props.row.premises.name }}
                </b-table-column>
                <b-table-column
                  field="score_percentage"
                  label="Score"
                  width="10%"
                  :sortable="false"
                  centered
                >
                  <span v-if="props.row.score_percentage !== null && props.row.score_percentage !== undefined">
                    {{ Math.round(props.row.score_percentage * 100) }}%
                  </span>
                  <span v-else class="has-text-grey">
                    N/A
                  </span>
                </b-table-column>
                <b-table-column
                  field="template"
                  label="Template Name"
                  width="19%"
                  :sortable="false"
                >
                  {{ props.row.template }}
                </b-table-column>
                <b-table-column
                  field="auditor"
                  label="Auditor"
                  width="15%"
                  :sortable="false"
                >
                  {{ props.row.auditor }}
                </b-table-column>
                <b-table-column label="Actions">
                  <button
                    class="button is-small is-orange"
                    @click="select(props.row)"
                  >
                    <b-icon icon="edit"></b-icon>
                  </button>
                  <button
                    class="button is-small is-orange"
                    @click="generatePdf(props.index, props.row.uuid)"
                  >
                    <!-- <b-icon icon="file-pdf"></b-icon> -->
                    <DownloadIcon/>
                  </button>
                  <button
                    class="button is-small is-orange"
                    @click="deleteParsedAudit(props.index, props.row.uuid)"
                  >
                    <b-icon icon="delete"></b-icon>Delete
                  </button>
                </b-table-column>
              </template>
            </b-table>

            <div class="flex items-center justify-end gap-4">
              <div>
                <b-select v-model="perPage" @input="onPerPageChanged">
                  <option value="5">5 per page</option>
                  <option value="10">10 per page</option>
                  <option value="15">15 per page</option>
                  <option value="20">20 per page</option>
                </b-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import router from "@/routes";
import AppLayout from "@/layouts/AppLayout";
import API from "@/services/API";
import { sync } from "vuex-pathify";
import ImageLogo from "@/assets/images/respro-logo.svg";
import DownloadIcon from "vue-material-design-icons/Download.vue";
import { DxDateBox } from "devextreme-vue/date-box";

export default {
  name: "Home",
  components: {
    AppLayout,
    DownloadIcon,
    DxDateBox,
  },
  data() {
    return {
      defaultImage: ImageLogo,
      premisesSelecte: false,
      down: true,
      displayMode: "compact",
      pageSizes: ["10", "15", "25", "all"],
      showPageSizeSelector: true,
      showInfo: true,
      showNavButtons: true,
      totalRows: 0,
      perPage: 10,
      currentPage: 1,
      sortBy: "submit_time",
      sortDirection: "desc",
      tableKey: 0, // Key to force table re-render
      debounceTimeout: null,
    };
  },
  filters: {
    formatDate(value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Denver",
        timeStyle: "medium",
        dateStyle: "short",
      }).format(new Date(value));
    },
  },
  computed: {
    premises: sync("app/premises"),
    activeUserRole: sync("app/activeUserRole"),
    token: sync("app/session"),
    currentAuditIndex: sync("audit/currentAuditIndex"),
    audits: sync("audit/audits"),
    location: sync("audit/audits[:currentAuditIndex]@location"),
    selectedAuditUuid: sync("audit/selectedAuditUuid"),
    filters: sync("audit/filters"),
  },
  watch: {
    audits: {
      handler(newAudits, oldAudits) {
        // Audits updated
      },
      deep: true
    }
  },
  methods: {
    mapSortFieldToAPI(fieldName) {
      // Map frontend field names to backend API sort field names
      const fieldMapping = {
        'submit_time': 'date_submitted',
        'score_percentage': 'score_percentage',
        'uuid': 'uuid'
      };
      return fieldMapping[fieldName] || fieldName;
    },
    loadAudits() {
      const loadingComponent = this.$buefy.loading.open({ container: null });

      // Prepare filter object
      const filters = {};
      if (this.filters.fromDate) {
        filters.fromDate = this.formatDateForAPI(this.filters.fromDate);
      }
      if (this.filters.toDate) {
        filters.toDate = this.formatDateForAPI(this.filters.toDate);
      }
      if (this.filters.premise.trim()) {
        filters.premise = this.filters.premise.trim();
      }
      if (this.filters.templateName.trim()) {
        filters.templateName = this.filters.templateName.trim();
      }
      if (this.filters.auditor.trim()) {
        filters.auditor = this.filters.auditor.trim();
      }

      API.getAudits(
        this.token,
        this.currentPage,
        this.perPage,
        this.mapSortFieldToAPI(this.sortBy),
        this.sortDirection,
        filters
      )
        .then((responses) => {
          let data = responses.data;

          let audits = data.audits;
          
          this.totalRows = data.total_results;

          let parsedAudits = [];

          audits.forEach((item, index) => {
            parsedAudits.push({
              ...item,
              auditIndex: index,
              selectedPremises: item["premises"],
              body: [],
            });
          });

          this.audits = parsedAudits;

          loadingComponent.close();
        })
        .catch((error) => {
          API.APILog(error);
          loadingComponent.close();
          this.$buefy.dialog.alert("Error fetching audit information.");
        });
    },
    onSortChanged(field, order) {
      this.sortBy = field;
      this.sortDirection = order;
      this.currentPage = 1; // Reset to first page when sorting changes
      this.loadAudits();
    },
    onPageChanged(newPage) {
      this.currentPage = newPage;
      this.loadAudits();
    },
    onPerPageChanged(newPerPage) {
      this.perPage = newPerPage;
      this.loadAudits();
    },
    formatDateTime(datetime) {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Denver",
        timeStyle: "medium",
        dateStyle: "short",
      }).format(new Date(datetime));
    },
    select(premise) {
      if (premise) {
        this.currentAuditIndex = premise.auditIndex;
        this.selectedAuditUuid = premise.uuid;
        router.push({ name: "audit" });
      } else {
        this.$buefy.dialog.alert(
          "No locations found. If you expect to see a new location, try logging out first."
        );
      }
    },
    editRow(e) {
      this.currentAuditIndex = e.row.rowIndex;
      this.selectedAuditUuid = e.row.key;
      router.push({ name: "audit" });
    },
    deleteRow(e) {
      this.deleteParsedAudit(e.row.data.dataIndex, e.row.key);
    },
    generatePdf(index, uuid) {
      const loadingComponent = this.$buefy.loading.open({ container: null });
      API.generate_pdf_and_send(this.token, uuid, true)
        .then((responses) => {
          const downloadUrl = responses.data.download_link;
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", downloadUrl.split("/").pop());
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          loadingComponent.close();
        })
        .catch((error) => {
          API.APILog(error);
          loadingComponent.close();
          this.$buefy.dialog.alert("Error fetching account information.");
        });
    },
    generatePdfDevextreme(e) {
      this.generatePdf(e.row.data.dataIndex, e.row.key);
    },
    deleteParsedAudit(index, parsedUuid) {
      if (parsedUuid) {
        this.$buefy.dialog.confirm({
          title: "Deleting audit",
          message:
            "Are you sure you want to <b>delete</b> an audit? This action cannot be undone.",
          confirmText: "Delete",
          type: "is-danger",
          hasIcon: true,
          onConfirm: () => {
            API.deleteParsedAudit(this.token, parsedUuid)
              .then((responses) => {
                if (responses.status == 200 || responses.status == 202) {
                  // Check if we need to go back a page (if we deleted the last item on current page)
                  const auditsOnCurrentPage = this.audits.length;
                  
                  if (auditsOnCurrentPage === 1 && this.currentPage > 1) {
                    this.currentPage--;
                  }
                  
                  // Reload the audits data from server
                  this.loadAudits();
                  this.$buefy.toast.open({
                    message: "An audit has been deleted successfully",
                    type: "is-success",
                  });
                }
              })
              .catch((error) => {
                API.APILog(error);
              });
          },
        });
      } else {
        this.$buefy.dialog.alert(
          "No audit found. If you expect to see a new audit, try logging out first."
        );
      }
    },
    onBack() {
      this.currentAuditIndex = -1;
      this.$router.push({ name: "home" });
    },
    getPremisesName: function (premiseUuid) {
      let val = this.premises.find((item) => item.uuid === premiseUuid);

      return val ? val["name"] : "";
    },
    formatDateForAPI(date) {
      if (!date) return null;
      // Handle both Date objects and string dates
      const dateObj = date instanceof Date ? date : new Date(date);
      return dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
    },
    formatDate(date) {
      if (!date) return "";
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
      }).format(date);
    },
    onFilterChange() {
      this.currentPage = 1; // Reset to first page when filters change
      this.updateUrlParams();
      this.loadAudits();
    },
    onDateFilterChange(e) {
      this.onFilterChange();
    },
    debouncedFilterChange() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.onFilterChange();
      }, 500);
    },
    clearFilters() {
      this.filters.fromDate = null;
      this.filters.toDate = null;
      this.filters.premise = '';
      this.filters.templateName = '';
      this.filters.auditor = '';
      this.updateUrlParams();
      this.onFilterChange();
    },
    updateUrlParams() {
      const query = {};
      if (this.filters.fromDate) {
        query.fromDate = this.formatDateForAPI(this.filters.fromDate);
      }
      if (this.filters.toDate) {
        query.toDate = this.formatDateForAPI(this.filters.toDate);
      }
      if (this.filters.premise) {
        query.premise = this.filters.premise;
      }
      if (this.filters.templateName) {
        query.templateName = this.filters.templateName;
      }
      if (this.filters.auditor) {
        query.auditor = this.filters.auditor;
      }
      
      // Update URL without triggering navigation
      this.$router.replace({ query }).catch(() => {});
    },
    loadFiltersFromUrl() {
      const query = this.$route.query;
      
      if (query.fromDate) {
        this.filters.fromDate = new Date(query.fromDate);
      }
      if (query.toDate) {
        this.filters.toDate = new Date(query.toDate);
      }
      if (query.premise) {
        this.filters.premise = query.premise;
      }
      if (query.templateName) {
        this.filters.templateName = query.templateName;
      }
      if (query.auditor) {
        this.filters.auditor = query.auditor;
      }
    },
  },

  created() {
    this.loadFiltersFromUrl();
    this.loadAudits();
  },
};
</script>
<style src="./Home.css" scoped></style>